<template>
  <main class="media-page py-16 bg-white min-h-screen">
    <div class="max-w-7xl mx-auto px-4 lg:px-8">
      <h1 class="text-3xl lg:text-4xl font-bold text-gray-900 text-center mb-4">{{ $t('media.title') }}</h1>
      <div class="w-24 h-0.5 bg-gray-200 mx-auto mb-8"></div>

      <!-- 加载状态 -->
      <div v-if="loading" class="flex justify-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>

      <!-- 空状态 -->
      <div v-else-if="mediaReports.length === 0" class="text-center py-12">
        <div class="text-gray-400 mb-4">
          <svg class="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"></path>
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">{{ $t('media.emptyTitle') }}</h3>
        <p class="text-gray-500">{{ $t('media.emptyDescription') }}</p>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="error" class="text-center py-12">
        <p class="text-red-600 mb-4">{{ error }}</p>
        <button
          @click="loadMediaReports"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {{ $t('media.retry') }}
        </button>
      </div>

      <!-- 媒体报道列表 -->
      <div v-else class="timeline space-y-8">
        <article
          v-for="item in mediaReports"
          :key="item.id"
          class="media-item cursor-pointer"
          @click="viewDetail(item)"
        >
          <div class="media-date text-gray-500 text-sm mb-2">
            {{ formatDate(item.publish_date) }}
          </div>
          <h2 class="media-title text-blue-600 text-lg lg:text-xl font-semibold mb-2 hover:text-blue-800 transition-colors">
            {{ item.title }}
          </h2>
          <div class="media-body text-gray-800 text-sm leading-relaxed">
            {{ item.summary || item.content.substring(0, 200) + '...' }}
          </div>
          <div v-if="item.source" class="mt-3 text-xs text-gray-500">
            {{ $t('media.source') }}：{{ item.source }}
          </div>
        </article>
      </div>

    </div>
  </main>
</template>

<script>
export default {
  name: 'MediaNews',
  data() {
    return {
      mediaReports: [],
      loading: false,
      error: null,
      currentLang: 'cn' // 默认中文
    }
  },
  mounted() {
    // 初始化语言设置
    const stored = localStorage.getItem('locale')
    if (stored) {
      this.currentLang = this.mapLocaleToApiLang(stored)
    }
    this.loadMediaReports()
    // 监听语言变化
    window.addEventListener('localeChange', this.onLocaleChange)
  },
  beforeDestroy() {
    window.removeEventListener('localeChange', this.onLocaleChange)
  },
  methods: {
    mapLocaleToApiLang(locale) {
      // 将LayoutTopNav的语言代码转换为API所需的格式
      const mapping = {
        'zh': 'cn',      // 中文 -> cn
        'zh-Hant': 'tw', // 繁体中文 -> tw
        'en': 'en'       // 英文 -> en
      }
      return mapping[locale] || 'cn'
    },

    onLocaleChange(event) {
      // 监听语言切换事件，更新语言状态并重新加载数据
      const newLocale = event.detail.locale
      this.currentLang = this.mapLocaleToApiLang(newLocale)
      console.log('Language changed:', newLocale, '-> API lang:', this.currentLang)
      this.$nextTick(() => {
        this.loadMediaReports()
      })
    },

    async loadMediaReports() {
      try {
        this.loading = true
        this.error = null

        // 调用公开API获取媒体报道列表
        const response = await fetch(`/api/public/media?page=1&page_size=50&lang=${this.currentLang}`)
        const data = await response.json()

        if (data.code === 0) {
          this.mediaReports = data.data.list || []
        } else {
          throw new Error(data.message || '获取媒体报道失败')
        }
      } catch (error) {
        console.error('加载媒体报道失败:', error)
        this.error = error.message || '加载媒体报道失败'
      } finally {
        this.loading = false
      }
    },

    viewDetail(item) {
      // 跳转到详情页面
      this.$router.push(`/media/${item.id}?lang=${this.currentLang}`)
    },


    formatDate(dateString) {
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      const weekdayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
      const weekdayKey = weekdayKeys[date.getDay()]
      const weekday = this.$t(`weekdays.${weekdayKey}`)

      return `${year}.${month}.${day} ${weekday}`
    }
  }
}
</script>

<style scoped>
.media-page {
  background: #ffffff;
}
.media-item {
  padding: 18px 12px;
  border-radius: 8px;
}
.media-title {
  color: #1e3a8a; /* blue-700 like */
}
.media-body {
  color: #333333;
}
@media (min-width: 1024px) {
  .media-item {
    padding: 20px 24px;
  }
}
</style>


